/**
 * ImageHighlightOverlay — SVG overlay for highlighting field regions on images (KAN-82 Phase 2).
 *
 * Simplified version of PdfHighlightOverlay for single images (no pagination).
 * Coordinates are scaled from the original image size to the displayed size.
 */

import React from 'react';
import { FieldCoordinates } from '../../types/tradeImageCapture';

export interface ImageFieldHighlight {
  fieldName: string;
  coordinates: FieldCoordinates;
  confidence: number;
  isSelected: boolean;
}

interface ImageHighlightOverlayProps {
  highlights: ImageFieldHighlight[];
  imageWidth: number;
  imageHeight: number;
  naturalWidth: number;
  naturalHeight: number;
  onClick?: (fieldName: string) => void;
}

const getConfidenceColor = (confidence: number): string => {
  if (confidence >= 0.9) return '#10b981'; // Green
  if (confidence >= 0.6) return '#f59e0b'; // Amber
  return '#ef4444'; // Red
};

const ImageHighlightOverlay: React.FC<ImageHighlightOverlayProps> = ({
  highlights,
  imageWidth,
  imageHeight,
  naturalWidth,
  naturalHeight,
  onClick,
}) => {
  if (highlights.length === 0 || naturalWidth === 0 || naturalHeight === 0) {
    return null;
  }

  const scaleX = imageWidth / naturalWidth;
  const scaleY = imageHeight / naturalHeight;

  const pixelPadding = 4;
  const expansionFactor = 0.08; // 8% expansion for visibility

  return (
    <svg
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: imageWidth,
        height: imageHeight,
        pointerEvents: 'none',
      }}
    >
      {highlights.map((highlight, index) => {
        const color = getConfidenceColor(highlight.confidence);
        const isSelected = highlight.isSelected;

        const paddedWidth = highlight.coordinates.width * (1 + expansionFactor);
        const paddedHeight = highlight.coordinates.height * (1 + expansionFactor);

        const scaledX = (highlight.coordinates.x * scaleX) - pixelPadding;
        const scaledY = (highlight.coordinates.y * scaleY) - pixelPadding;
        const scaledWidth = (paddedWidth * scaleX) + (pixelPadding * 2);
        const scaledHeight = (paddedHeight * scaleY) + (pixelPadding * 2);

        return (
          <rect
            key={`${highlight.fieldName}-${index}`}
            x={scaledX}
            y={scaledY}
            width={scaledWidth}
            height={scaledHeight}
            fill={color}
            fillOpacity={isSelected ? 0.25 : 0.08}
            stroke={color}
            strokeWidth={isSelected ? 3 : 1.5}
            strokeDasharray={isSelected ? 'none' : '4 2'}
            rx="3"
            ry="3"
            style={{
              pointerEvents: onClick ? 'auto' : 'none',
              cursor: onClick ? 'pointer' : 'default',
              transition: 'fill-opacity 0.2s, stroke-width 0.2s',
            }}
            onClick={() => onClick && onClick(highlight.fieldName)}
          />
        );
      })}
    </svg>
  );
};

export default ImageHighlightOverlay;
